import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
} from "@mui/material";
import {
  DndContext,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import SortableTableRow from "./SortableTableRow";

const isUseRender = (render) => {
  return typeof render === "function";
};

const ReusableDndTable = ({
  columns,
  data: initialData,
  maxHeight = 300,
  getRowStyle = () => ({}),
  onRowClick = null,
  onDragEnd,
}) => {
  const columnWidth = `${100 / columns.length}%`;

  const [data, setData] = useState([...initialData]);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");

  useEffect(() => {
    setData(initialData);
  }, [initialData]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);

    const sortedData = [...data].sort((a, b) => {
      if (a[property] < b[property]) return isAsc ? -1 : 1;
      if (a[property] > b[property]) return isAsc ? 1 : -1;
      return 0;
    });

    setData(sortedData);
  };

  const renderCellContent = (column, item) => {
    if (isUseRender(column.render)) {
      return column.render(item);
    }
    return item[column.key];
  };

  // handle dnd
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 10 },
  });
  const sensors = useSensors(pointerSensor);

  return (
    <Paper
      sx={{ width: "100%", overflow: "hidden", border: "1px solid #e0e0e0" }}
    >
      <DndContext
        onDragEnd={onDragEnd}
        sensors={sensors}
        collisionDetection={closestCorners}
      >
        <TableContainer>
          <Table
            sx={{ overflow: "hidden" }}
            stickyHeader
            aria-label="sticky table"
          >
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.align || "left"}
                    sx={{
                      width: columnWidth,
                      fontWeight: "bold",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    {column.sortable !== false ? (
                      <TableSortLabel
                        active={orderBy === column.key}
                        direction={orderBy === column.key ? order : "asc"}
                        onClick={() => handleRequestSort(column.key)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          </Table>
        </TableContainer>
        <TableContainer sx={{ maxHeight: maxHeight, overflowY: "auto" }}>
          <Table sx={{ overflow: "hidden" }}>
            <TableBody>
              <SortableContext
                items={data.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                {data.map((item, index) => (
                  <SortableTableRow
                    key={item.id}
                    item={item}
                    index={index}
                    columns={columns}
                    onRowClick={onRowClick}
                    getRowStyle={getRowStyle}
                    renderCellContent={renderCellContent}
                    columnWidth={columnWidth}
                  />
                ))}
              </SortableContext>
            </TableBody>
          </Table>
        </TableContainer>
      </DndContext>
    </Paper>
  );
};

export default ReusableDndTable;
